import React from 'react';
import PageHeaderBand from '../ui/PageHeaderBand';
import Toolbar, { TOOLBAR_RIGHT_SLOT } from '../ui/Toolbar';
import { PHONE_FULL_BLEED } from '../../constants/phoneLayout';
import SearchInput from '../ui/SearchInput';
import Tabs from '../ui/Tabs';
import { HireStatusTabs } from './HireTableView';
import { CostSummaryInline } from './CostSummary';
import MobileTabSearchRow, { PHONE_HEADER_TOOLBAR } from '../MobileTabSearchRow';
import GroupControl from '../ui/GroupControl';
import { HIRES_TAB_LABEL_TEXT } from '../../constants/hiresViewTabs';

/**
 * THE page chrome of Department Hires and My Hires (2026-09-27) — the order
 * pages' TWO-BAND layout (`components/ui/CLAUDE.md`), shared by both hires
 * pages so it is written once:
 *
 *   All hires · 643 shown                                   ← PageHeaderBand, scrolls away
 *   ─────────────────────────────────────────────────────────── (sticky from here)
 *   [🔍 Search hires…] [Table│Kanban│Gantt│Calendar]           [Actions ▾]
 *   All 643 · Upcoming 0 · On hire 643 · Ending soon 567 · Off hire 0   Expected … Current … Variance …
 *
 * ── Per-page variations from Department Orders, each justified ─────────
 * - TWO sticky lines, not one: Dept Orders' queue tabs share the search row,
 *   but the hires bar already carries a VIEW switcher there (Table/Kanban/
 *   Gantt/Calendar), so the STATUS tabs — which exist in Table view only —
 *   take their own line underneath, inside the same sticky `ui/Toolbar` (both
 *   are what you reach for mid-scroll, the band rule's test).
 * - `Actions ▾` sits on the STICKY bar (Dept Orders' 2026-09-12 scoped
 *   exception, on the same premise: the band holds only `context` and scrolls
 *   away, so an Actions menu there is unreachable mid-scroll).
 * - The cost figures ride the status line, right-aligned (owner, 2026-09-27:
 *   the block *"should either go on this line, right aligned … or this
 *   line"*). The TAB line is the one with room: its five tabs are narrower than
 *   search + four view tabs + `Actions ▾`. In every other view the full
 *   `CostSummary` banner stays where it was (`aboveToolbar`).
 * - No `Sort ▾` / `Filters ▾`: the Table's headers sort, and the hires pages
 *   have no filter model beyond search and the status tabs.
 *
 * The search box stays CLIENT-SIDE (`hooks/useHireSearch.js`), `debounceMs={0}`.
 * The view strip (moved here verbatim from both pages, 2026-09-27): `active`
 * is the page's `effectiveTab`, NOT `activeTab` — below `md` a stored "gantt"
 * must still highlight Kanban. `persistKey` is deliberately NOT passed: it
 * would write whatever `active` is on every change, and a bare desktop→mobile
 * resize changes `effectiveTab` without a click, silently overwriting a real
 * "gantt" preference. The write stays manual, in the page's `onViewChange`,
 * keyed to the id actually clicked. Testids `view-strip` / `view-tab-<id>`.
 *
 * ── 📱 PHONE, TABLE VIEW (below `md`, 2026-10-01) ─────────────────────
 * A mobile audit measured the sticky bars covering 49% of a 360×740 screen.
 * Below `md`, in the Table view, ONLY the shared phone header is sticky
 * (2026-10-03; the "All hires · N shown" band is hidden on a phone):
 *
 *   [Table│Kanban│Calendar]   Expected … Current … ← scrolls away
 *   ─────────────────────────────────────────── (sticky from here)
 *   All 643 · Upcoming 0 · On hire 643 …
 *   [🔍]                                  [Actions ▾]
 *
 * THE phone header, `components/MobileTabSearchRow.js`. Kanban / Calendar on a
 * phone keep the chrome above unchanged — no status tabs to pin there.
 */
export default function HiresListChrome({
  toolbarRef,
  contextLabel,
  shownCount,
  searchTerm,
  onSearchChange,
  viewTabs,
  activeView,
  onViewChange,
  list,
  // OPTIONAL `(id) => void` for the status tabs — the page passes its
  // dirty-edit guard (`useHiresViewSwitch().continueWhenClean`, 2026-09-28);
  // absent, the tabs call `list.setTab` as before.
  onStatusTabChange = null,
  summary,
  actions = null,
  aboveToolbar = null,
  // `useIsMobileOrderViewport()` from the page — see the PHONE block above.
  phone = false,
  // Phone only: false hides the money figures (My Hires, owner 2026-10-01 —
  // "the individual likely doesn't care"; Dept Hires keeps them).
  showPhoneSummary = true,
}) {
  const isTable = activeView === 'table';
  const viewStrip = (
    <Tabs
      scrollHints
      tabs={viewTabs}
      active={activeView}
      onChange={onViewChange}
      idPrefix="view"
      ariaLabel="Hires view"
      bare
      className="w-fit"
    />
  );
  if (phone && isTable && list) {
    return (
      <>
        {/* 📱 The context line is hidden on a phone (the tab badges count)
            and Actions ▾ rides the phone header's row 2 (2026-10-03), so
            this band renders nothing below `md`. */}
        <PageHeaderBand
          testId="hires-header-band"
          hideContextOnPhone
          context={<>{contextLabel} &middot; {shownCount} shown</>}
        />
        {aboveToolbar}
        <Toolbar className={PHONE_HEADER_TOOLBAR} ref={toolbarRef} data-testid="hires-toolbar">
          {/* The shared phone header: row 1 the status tabs, row 2 🔍 ·
              Actions ▾ (one row — 🔍 + tabs — when there are no actions). */}
          <MobileTabSearchRow
            idPrefix="hires"
            searchTestIdPrefix="hires"
            searchText={searchTerm}
            onSearchChange={onSearchChange}
            searchPlaceholder="Search hires…"
            searchLabel="Search hires"
            tabStrip={<HireStatusTabs list={list} onTabChange={onStatusTabChange} bare phone className="flex-1 min-w-0" />}
            view={(
              <GroupControl
                options={viewTabs}
                value={activeView}
                onChange={onViewChange}
                flatId="__none__"
                heading="View"
                closeOnSelect
                shrinkable
                ariaLabel="Hires view"
                title="Show the hires as a table, kanban or calendar"
                testId="hires-view"
                renderLabel={() => <span>View: {HIRES_TAB_LABEL_TEXT[activeView] || activeView}</span>}
              />
            )}
            actions={actions}
          />
        </Toolbar>
        {/* The money sits UNDER the pinned header (scrolls with the list) so
            the header starts at the same height as every other list page
            (owner 2026-10-03). The view switch is `View ▾` on row 2. */}
        {showPhoneSummary && (
          <div className="flex items-center min-w-0 pb-1" data-testid="hires-mobile-view-row">
            <CostSummaryInline summary={summary} className="ml-auto" />
          </div>
        )}
      </>
    );
  }
  return (
    <>
      <PageHeaderBand
        testId="hires-header-band"
        context={<>{contextLabel} &middot; {shownCount} shown</>}
      />
      {aboveToolbar}
      <Toolbar className={PHONE_FULL_BLEED} ref={toolbarRef} data-testid="hires-toolbar">
        <SearchInput
          value={searchTerm}
          onChange={onSearchChange}
          placeholder="Search hires…"
          ariaLabel="Search hires"
          debounceMs={0}
          testId="hires-search"
        />
        {viewStrip}
        {actions ? <div className={TOOLBAR_RIGHT_SLOT}>{actions}</div> : null}
        {isTable && list ? (
          /* `basis-full` puts the status line on its OWN line under the search
             row at every width. `flex-wrap` + the money's `ml-auto`: on a wide
             screen the figures sit right-aligned beside the tabs; on a phone
             they wrap BELOW the tabs rather than squeezing them. */
          <div className="basis-full flex flex-wrap items-center gap-x-4 gap-y-1 min-w-0" data-testid="hires-status-line">
            <HireStatusTabs list={list} onTabChange={onStatusTabChange} bare className="min-w-0" />
            <CostSummaryInline summary={summary} className="ml-auto" />
          </div>
        ) : null}
      </Toolbar>
    </>
  );
}
